import { X, BookOpen, Volume2, Sparkles, BookPlus, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import type { Definition } from '@/lib/dictionary';
import { useAppStore } from '@/lib/store';

interface DefinitionPopupProps {
    definition: Definition | null;
    word: string;
    position: { x: number; y: number };
    onClose: () => void;
    onReplace: (newWord: string) => void;
    suggestions?: string[];
    themeColors: any;
}

export function DefinitionPopup({ definition, word, position, onClose, onReplace, suggestions = [], themeColors }: DefinitionPopupProps) {
    if (!word) return null;

    // Collect all synonyms from meanings and definitions
    const allSynonyms = Array.from(new Set(
        (definition?.meanings || []).flatMap(m => [
            ...(m.synonyms || []),
            ...(m.definitions || []).flatMap(d => d.synonyms || [])
        ])
    )).slice(0, 10); // Limit to 10 for UI

    const { activeNovelId, novels, addCustomWord } = useAppStore();
    const activeNovel = novels.find(n => n.id === activeNovelId);
    const isInDictionary = activeNovel?.customWords?.some(cw => cw.word.toLowerCase() === word.toLowerCase());

    const handleAddToDictionary = () => {
        if (activeNovelId) {
            const firstDef = definition?.meanings?.[0]?.definitions?.[0]?.definition || '';
            addCustomWord(activeNovelId, word, firstDef);
        }
    };


    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 10 }}
                className="fixed z-[100] w-72 rounded-2xl shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5)] border-2 overflow-hidden backdrop-blur-xl"
                style={{
                    left: Math.min(window.innerWidth - 300, Math.max(20, position.x - 144)),
                    top: Math.min(window.innerHeight - 300, Math.max(100, position.y - 120)),
                    backgroundColor: themeColors.bgSecondary + 'f2', // Increase opacity
                    borderColor: themeColors.border,
                    color: themeColors.text,
                    boxShadow: '0 0 0 1px rgba(255,255,255,0.1) inset' // Subtle inner glow
                }}
            >
                <div className="p-4 space-y-3">
                    <div className="flex items-start justify-between">
                        <div className="space-y-1">
                            <h3 className="text-xl font-bold tracking-tight capitalize">{word}</h3>
                            {definition?.phonetic && (
                                <p className="text-xs font-mono opacity-60 flex items-center gap-1">
                                    <Volume2 className="w-3 h-3" />
                                    {definition.phonetic}
                                </p>
                            )}
                        </div>
                        <button
                            onClick={onClose}
                            className="p-1 rounded-full hover:bg-neutral-500/10 transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="max-h-64 overflow-y-auto pr-1 space-y-4 custom-scrollbar">
                        {!definition && suggestions.length === 0 ? (
                            <div className="py-4 text-center opacity-50 italic text-sm">
                                No definition or suggestions found.
                            </div>
                        ) : null}

                        {suggestions.length > 0 && (
                            <div className="space-y-2">
                                <div className="flex items-center gap-2 px-2 py-0.5 rounded-md bg-red-500/10 w-fit">
                                    <Sparkles className="w-3 h-3 text-red-500" />
                                    <span className="text-[10px] font-black uppercase tracking-widest text-red-500">
                                        Spelling Suggestions
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {suggestions.map((sug, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => {
                                                onReplace(sug);
                                                onClose();
                                            }}
                                            className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-[11px] font-black hover:bg-red-500 hover:scale-105 transition-all border border-red-400/50 shadow-md active:scale-95"
                                        >
                                            {sug}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {definition && (
                            definition.meanings.map((meaning, idx) => (
                                <div key={idx} className="space-y-2">
                                    <div className="flex items-center gap-2 px-2 py-0.5 rounded-md bg-amber-500/10 w-fit">
                                        <BookOpen className="w-3 h-3 text-amber-600" />
                                        <span className="text-[10px] font-black uppercase tracking-widest text-amber-600">
                                            {meaning.partOfSpeech}
                                        </span>
                                    </div>
                                    <div className="space-y-2">
                                        {meaning.definitions.slice(0, 2).map((def, dIdx) => (
                                            <div key={dIdx} className="space-y-1">
                                                <p className="text-sm leading-relaxed">
                                                    {def.definition}
                                                </p>
                                                {def.example && (
                                                    <p className="text-xs italic opacity-60 border-l-2 border-amber-500/30 pl-2">
                                                        "{def.example}"
                                                    </p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))
                        )}
                        {allSynonyms.length > 0 && (
                            <div className="space-y-2 pt-3 border-t border-current opacity-30">
                                <div className="flex items-center gap-2 px-2 py-0.5 rounded-md bg-cyan-500/10 w-fit">
                                    <Sparkles className="w-3 h-3 text-cyan-600" />
                                    <span className="text-[10px] font-black uppercase tracking-widest text-cyan-600">
                                        Thesaurus
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {allSynonyms.map((syn, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => {
                                                onReplace(syn);
                                                onClose();
                                            }}
                                            className="px-3 py-1.5 rounded-lg bg-cyan-600 text-white text-[11px] font-black hover:bg-cyan-500 hover:scale-105 transition-all border border-cyan-400/50 shadow-md active:scale-95"
                                        >
                                            {syn}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="pt-3 border-t border-current opacity-30">
                            {isInDictionary ? (
                                <div className="flex items-center gap-2 text-green-600 px-2 py-1.5 rounded-lg bg-green-500/10">
                                    <Check className="w-4 h-4" />
                                    <span className="text-[10px] font-black uppercase tracking-widest">
                                        In Author's Dictionary
                                    </span>
                                </div>
                            ) : (
                                <button
                                    onClick={handleAddToDictionary}
                                    className="w-full flex items-center justify-center gap-2 py-2 px-3 rounded-xl bg-amber-500/20 hover:bg-amber-500 text-amber-700 hover:text-black transition-all group border border-amber-500/30"
                                >
                                    <BookPlus className="w-4 h-4 group-hover:scale-110 transition-transform" />
                                    <span className="text-[10px] font-black uppercase tracking-widest">
                                        Add to Dictionary
                                    </span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="px-4 py-2 bg-amber-500/10 text-[10px] uppercase font-bold tracking-tighter opacity-70 flex justify-between items-center">
                    <span>Double-click to define</span>
                    <span className="text-amber-500">Antigravity Dictionary</span>
                </div>
            </motion.div >
        </AnimatePresence >
    );
}
